import { IFormSchemaSection } from "../types";

export interface IFormStep {
  key: string;
  title: string;
  sections: IFormSchemaSection[];
  // True when any section or field within the step carries a warning-level
  // note. Drives the amber warning icon on the step in the nav rail (the
  // form-level warning is OR-ed onto the first step by the renderer).
  hasWarning: boolean;
}

// Matches a leading wizard-step token in a section key, e.g. "step1c" / "step2".
// Sections sharing the same numeric step coalesce into one wizard step so a
// step can hold multiple cards (e.g. "step1a" + "step1b" → "Step 1").
const STEP_KEY_PATTERN = /^step(\d+)/i;

const stepKeyOf = (section: IFormSchemaSection): string | undefined => {
  // Prefer an explicit grouping when the schema provides one.
  if (section.step) return section.step;
  const match = STEP_KEY_PATTERN.exec(section.key);
  return match ? `step${match[1]}` : undefined;
};

/**
 * Groups a form's flat `sections` into ordered wizard steps for the stepper UI.
 *
 * Grouping precedence (per section):
 *  1. explicit `section.step`
 *  2. a leading `stepN` token in `section.key` (numeric, so `step1a/b/c` merge)
 *  3. otherwise the section stands alone as its own step
 *
 * Order is first-seen and preserved. When no section carries step information
 * the result is one step per section; with 0–1 sections it is a single step —
 * callers treat `<= 1` step as "render single-page" (no stepper).
 */
export const selectorFormSteps = (
  sections: IFormSchemaSection[]
): IFormStep[] => {
  const steps: Omit<IFormStep, "hasWarning">[] = [];
  const indexByKey = new Map<string, number>();

  sections.forEach(section => {
    const groupKey = stepKeyOf(section) ?? section.key;
    const existing = indexByKey.get(groupKey);
    if (existing !== undefined) {
      steps[existing].sections.push(section);
      return;
    }
    indexByKey.set(groupKey, steps.length);
    steps.push({
      key: groupKey,
      title: section.stepTitle ?? section.title,
      sections: [section]
    });
  });

  return steps.map(step => ({
    ...step,
    hasWarning: step.sections.some(
      section =>
        section.warnings.length > 0 ||
        section.fields.some(field => field.warnings.length > 0)
    )
  }));
};
